/*
 * Title:     	drop.js
 * Description: Accessible drop menus
 * Author:      Mark Croxton, mcroxton@hallmark-design.co.uk
 * Copyright:   Hallmark Design
 * Updated:     March 22 2019
*/

let Drop = (function() {

    'use strict';

    function init(elm, closeOutside = 'mouseleave', doDelay = 200 ) {

        let _self = this;

        let _target = document.querySelector(elm);
        let _currentPanel; // the currently active panel
        let dropId = _target.dataset.dropId;
        let _parents = _target.querySelectorAll('[data-drop-open][data-drop-id="'+dropId+'"] > [aria-controls]');
        let _closers = _target.querySelectorAll('[data-drop-close][data-drop-id="'+dropId+'"]');

        let _active = false;
        let _initialDelay = doDelay;

        let doOpenPanel, doClosePanel, doOpenPanelComplete = null;

        for (let i=0; i < _parents.length; i++) {

            if (_parents[i].tagName === 'A' && ! _parents[i].parentNode.dataset.dropBtn) {
                // anchors
                _parents[i].addEventListener("mouseenter", function (e) {
                    _active = true;
                    openPanel(e.target);
                    let bounds = _parents[i].getBoundingClientRect();
                    _parents[i].style.setProperty('--mouse-x', e.clientX - bounds.left + "px");
                });
                _parents[i].addEventListener("click", function (e) {
                    _active = false; // prevent drop menus opening if we're about to navigate to the link
                });
            } else {

                _active = true;
                _initialDelay = 0;

                // buttons, or anchors treated like buttons
                _parents[i].addEventListener("click", function (e) {
                    e.preventDefault();

                    if (e.target.getAttribute('aria-expanded') === 'true') {
                        closePanels(0);
                    } else {
                        openPanel(e.target);
                    }
                });

                // allow user to press the ESCAPE key to close any open regions
                window.addEventListener("keyup", function(e){
                    let key = e.key || e.keyCode;
                    if (key === 'Escape' || key === 'Esc' || key === 27) {
                        closePanels(0);
                    }
                });
            }
        }

        if (closeOutside === 'mouseleave') {
            _target.addEventListener("mouseleave", function (e) {
                _active = false;
                closePanels(0);
            });

            for (let i=0; i < _closers.length; i++) {
                _closers[i].addEventListener("mouseenter", function (e) {
                    closePanels(0);
                });
            }
        } else if (closeOutside === 'click') {
            for (let i = 0; i < _closers.length; i++) {
                _closers[i].addEventListener("click", function (e) {
                    closePanels(0);
                });
            }
        }

        function openPanel(obj) {

            // cancel any pending reveals/hides
            if(doOpenPanel){
                clearTimeout(doOpenPanel);
                doOpenPanel = null;
            }
            if(doClosePanel) {
                clearTimeout(doClosePanel);
                doClosePanel = null;
            }
            if(doOpenPanelComplete) {
                clearTimeout(doOpenPanelComplete);
                doOpenPanelComplete = null;
            }

            let _panel = document.querySelector('#' + obj.getAttribute('aria-controls'));

            if (_panel.getAttribute('aria-hidden') === 'true') {

                // how quickly to switch panels?
                let delay = _initialDelay;
                let tabIndexes;

                if (_target.querySelector('[aria-hidden="false"]') != null) {
                    // a menu panel is already open
                    delay = 50;
                }

                doOpenPanel = setTimeout(function () {
                    if (_active) {

                        // close all buttons and associated panels
                        for (let i=0; i < _parents.length; i++) {
                            _parents[i].setAttribute('aria-expanded', 'false');
                            let panelId = _parents[i].getAttribute(['aria-controls']);
                            let panelObj = _target.querySelector('#' + panelId);
                            panelObj.setAttribute('aria-hidden', 'true');

                            // reset tabindexes
                            tabIndexes = panelObj.querySelectorAll('[tabindex="0"]');
                            for (let i=0; i < tabIndexes.length; i++) {
                                tabIndexes[i].setAttribute("tabindex", "-1");
                            }

                        }

                        // open the new panel
                        _panel.setAttribute('aria-hidden', 'false');
                        obj.setAttribute('aria-expanded', 'true');

                        // activate tabindexes...
                        tabIndexes = _panel.querySelectorAll('[tabindex="-1"]');
                        for (let i=0; i < tabIndexes.length; i++) {
                            tabIndexes[i].setAttribute("tabindex", "0");
                        }

                        // ..but tabindex for links in nested regions should remain inactive
                        let tabIndexesNested = _panel.querySelectorAll('[aria-hidden="true"] [tabindex="0"]');
                        for (let i=0; i < tabIndexesNested.length; i++) {
                            tabIndexesNested[i].setAttribute("tabindex", "-1");
                        }

                        _currentPanel = _target;
                    }

                }, delay);

                doOpenPanelComplete = setTimeout(function () {
                    if (_active) {
                        if (!_target.classList.contains(_target.dataset.active)) {
                            _target.classList.add(_target.dataset.active);
                        }
                    }
                }, delay);

            }
        }

        function closePanels(delay) {

            // delay to close panels
            delay = typeof delay !== 'undefined' ? delay : 200;

            //console.log('closePanels');

            // cancel any pending reveals/hides
            if(doOpenPanel){
                clearTimeout(doOpenPanel);
                doOpenPanel = null;
            }
            if(doClosePanel) {
                clearTimeout(doClosePanel);
                doClosePanel = null;
            }
            if(doOpenPanelComplete) {
                clearTimeout(doOpenPanelComplete);
                doOpenPanelComplete = null;
            }

            doClosePanel = setTimeout(function () {

                // close all buttons and associated panels
                for (let i=0; i < _parents.length; i++) {
                    _parents[i].setAttribute('aria-expanded', 'false');
                    let panelId = _parents[i].getAttribute(['aria-controls']);
                    let panelObj = _target.querySelector('#' + panelId);
                    panelObj.setAttribute('aria-hidden', 'true');

                    // reset tabindexes
                    let tabIndexes = panelObj.querySelectorAll('[tabindex="0"]');
                    for (let i=0; i < tabIndexes.length; i++) {
                        tabIndexes[i].setAttribute("tabindex", "-1");
                    }

                }

                if (_target.classList.contains( _target.dataset.active)) {
                    _target.classList.remove(_target.dataset.active);
                }
            }, delay);
        }
    }

    return {
        init: init
    };

})();

export default Drop;